import { useEffect, useState } from 'react';

import { httpStatusCodes } from '@common/Constants';
import Logger from '@common/Logger.js';
import { EVENT_DETAILS, CONSENT } from '@common/network';
import { getByPathAndParams } from '@services/BaseApi';

import { addStandardPrice, hasEventAlreadyStarted } from '../Util';

const useDetailsTabs = (eventUuid, bookingUuid, participantId) => {
  const [event, setEvent] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [bookingData, setBookingData] = useState({});
  const [consents, setConsents] = useState([]);
  const [bookingParticipantsData, setBookingParticipantsData] = useState({});
  const [isInvalidUrl, setIsInvalidUrl] = useState(false);
  const [isHiddenEvent, setIsHiddenEvent] = useState();
  const [isBookingDisabled, setIsBookingDisabled] = useState(false);

  const checkIsCatalogueIsNotVisibleAndIsNotPublic = (myEvent) => {
    return !myEvent?.isMeaCatalogVisible && !myEvent?.isPublic; // catalog status & public registration in TEM
  };

  const checkIsPublicRegistrationFalse = (myEvent) => {
    return !myEvent?.isPublic;
  };

  const isEventAlreadyStarted = hasEventAlreadyStarted(event);

  const fetchEvent = async () => {
    try {
      const response = await getByPathAndParams({
        path: EVENT_DETAILS.GET_EVENT_DETAILS,
        pathVariables: { eventUuid: eventUuid },
        params: { participantUuid: participantId },
        temApi: true
      });

      if (response?.status === httpStatusCodes.ok) {
        try {
          const myEvent = response?.data?.event || response?.data;
          if (checkIsCatalogueIsNotVisibleAndIsNotPublic(myEvent)) {
            setIsHiddenEvent(true);
          } else if (checkIsPublicRegistrationFalse(myEvent)) {
            setIsBookingDisabled(true);
            setEvent(addStandardPrice(myEvent));
          } else {
            setEvent(addStandardPrice(myEvent));
          }
        } catch (e) {
          console.error(e);
        }
        setIsInvalidUrl(false);
      }
    } catch (err) {
      if (err?.status === 404 || err?.status === 400) {
        setIsInvalidUrl(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBilling = async () => {
    try {
      const response = await getByPathAndParams({
        path: EVENT_DETAILS.GET_BILLING_DATA,
        pathVariables: { eventId: eventUuid, bookingUuid: bookingUuid },
        temApi: true
      });

      if (response?.status === httpStatusCodes.ok) {
        setBookingData(response.data);
      }
    } catch (err) {
      Logger.error(err);
    }
  };

  const fetchConsents = async () => {
    try {
      const { data, status } = await getByPathAndParams({
        path: CONSENT.GET_ALL_CONSENTS,
        params: {
          allLanguages: false,
          filters: {
            advancedFilters: [
              { key: 'type', value: 'CONSENT', operation: 'equal' },
              { key: 'eventId', value: event?.id, operation: 'equal' }
            ]
          }
        },
        temApi: true
      });
      if (status === httpStatusCodes.ok) {
        setConsents(data?.content ?? []);
      }
    } catch (err) {
      Logger.error(err);
    }
    return [];
  };

  const fetchParticipants = async () => {
    try {
      const response = await getByPathAndParams({
        path: EVENT_DETAILS.GET_PARTICIPANT_DATA,
        pathVariables: { eventUuid, bookingUuid },
        temApi: true
      });

      if (response?.status === httpStatusCodes.ok) {
        setBookingParticipantsData(response?.data?.content ?? null);
      }
    } catch (err) {
      Logger.error(err);
    }
  };

  useEffect(() => {
    const fetchEventAs = async () => {
      await fetchEvent();
    };
    if (eventUuid) {
      fetchEventAs();
    }
  }, [eventUuid, participantId]);

  return {
    fetchBilling,
    fetchParticipants,
    bookingData,
    bookingParticipantsData,
    fetchConsents,
    consents,
    fetchEvent,
    isLoading,
    event,
    isInvalidUrl,
    isHiddenEvent,
    isBookingDisabled,
    isEventAlreadyStarted
  };
};

export default useDetailsTabs;
